import { authenticatedJsonFetch } from '@perpay-web/authentication/authenticatedJsonFetch';
import { getData, useDataModule } from '@perpay-web/data-module/useDataModule';
import { checkoutUpdateOrderSuccess } from '@perpay-web/fintech/actions/entities/checkoutUpdateOrder';
import { OrderRequirement } from '@perpay-web/fintech/constants/orderRequirements';
import { OrderStatus } from '@perpay-web/fintech/constants/orderStatuses';
import { CheckoutStep } from '@perpay-web/fintech/constants/steps/checkoutSteps';
import { CHECKOUT_REQUIREMENTS_ENDPOINT } from '@perpay-web/fintech/constants/urls';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { checkoutUpdateUserInformationSuccess } from '@perpay-web/hooks/actions/checkoutUpdateUserInformation';
import { useActionObservableCallback } from '@perpay-web/hooks/useActionObservableCallback';
import { getRouterState, replace } from '@perpay-web/services/router';
import { hookAction$ } from '@perpay-web/services/singletons';
import { firstItemOf } from '@perpay-web/utils/arrayUtils';
import { createContextProvider } from '@perpay-web/utils/componentUtils';

const retrieveCheckoutUuid = () => {
    const { matches = [] } = getRouterState();
    if (!matches.length) {
        return null;
    }

    const { params } = firstItemOf(matches);
    const { uuid: checkoutUuid } = params;
    return checkoutUuid;
};

const getMissingRequirements = (state) => getData(state).missingRequirements;
const getStatus = (state) => getData(state).status;

const determineCheckoutStepFromMissingRequirements = (state) => {
    const missingRequirements = getMissingRequirements(state);

    if (missingRequirements.has(OrderRequirement.BILLING_INFORMATION)) {
        return CheckoutStep.BILLING_INFORMATION;
    }

    if (
        missingRequirements.has(OrderRequirement.SOCIAL_SECURITY) ||
        missingRequirements.has(OrderRequirement.DATE_OF_BIRTH)
    ) {
        return CheckoutStep.IDENTITY_VERIFICATION;
    }

    if (
        missingRequirements.has(OrderRequirement.PAYMENT_OPTIONS) ||
        missingRequirements.has(OrderRequirement.TERMS_AND_CONDITIONS)
    ) {
        return CheckoutStep.CONFIRM_ORDER;
    }

    return null;
};

export const getCheckoutStep = (state) => {
    const status = getStatus(state);

    if (status === OrderStatus.APPLICATION_STARTED) {
        return determineCheckoutStepFromMissingRequirements(state);
    }

    if (status === OrderStatus.PENDING) {
        return CheckoutStep.CHECKOUT_COMPLETE;
    }

    if ([OrderStatus.APPROVED, OrderStatus.AWAITING_PAYMENT].includes(status)) {
        return CheckoutStep.APPROVED;
    }

    if (status === OrderStatus.DENIED) {
        return CheckoutStep.DENIED;
    }

    if (status === OrderStatus.REPAYMENT) {
        return CheckoutStep.CREDIT_LINE_APPROVED;
    }

    if (status === OrderStatus.VERIFICATION) {
        return CheckoutStep.MANUAL_REVIEW;
    }

    return null;
};

const initialValue = { missingRequirements: new Set(), status: null };

const useFetchOrderRequirementsDataModule = () => {
    const dataModule = useDataModule(
        (checkoutUuid) =>
            authenticatedJsonFetch(
                CHECKOUT_REQUIREMENTS_ENDPOINT.replace(':uuid', checkoutUuid),
            ).then(({ missingRequirements, status }) => ({
                missingRequirements: new Set(missingRequirements),
                status,
            })),
        { initialValue },
    );

    const { dataRequest } = dataModule;

    useActionObservableCallback(
        hookAction$,
        checkoutUpdateUserInformationSuccess().type,
        (payload) => {
            const checkoutUuid = retrieveCheckoutUuid();
            if (!checkoutUuid) {
                return;
            }
            const { state } = dataModule;
            const checkoutStep = getCheckoutStep(state);
            const isIdentityVerificationStep =
                checkoutStep === CheckoutStep.IDENTITY_VERIFICATION;

            const { ssnIsVerified, ssnVerificationAttempts } = payload;
            const isFailedIDV = !ssnIsVerified && ssnVerificationAttempts >= 1;

            if (isIdentityVerificationStep && isFailedIDV) {
                const searchParams = new URLSearchParams();
                searchParams.set(
                    'next',
                    `${paths.checkout.path}${checkoutUuid}`,
                );

                replace({
                    pathname: paths.identityVerification.path,
                    search: searchParams.toString(),
                });
                return;
            }

            dataRequest(checkoutUuid);
        },
    );

    useActionObservableCallback(
        hookAction$,
        checkoutUpdateOrderSuccess().type,
        () => {
            const checkoutUuid = retrieveCheckoutUuid();
            if (!checkoutUuid) {
                return;
            }

            dataRequest(checkoutUuid);
        },
    );

    return dataModule;
};

const providerFactory = (Context) => {
    const Provider = ({ children }) => {
        const dataModule = useFetchOrderRequirementsDataModule();

        return (
            <Context.Provider value={dataModule}>{children}</Context.Provider>
        );
    };

    return Provider;
};

export const [
    UseFetchOrderRequirementsDataModuleProvider,
    useFetchOrderRequirementsContext,
] = createContextProvider(providerFactory);
